import {
  createEntityAdapter,
  createSlice,
  type EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type {
  Category,
  Segment,
  NormalizedSegments,
} from "@interfaces/segments";
import {
  createSegment,
  createSegmentAfterValidation,
  deleteSegment,
  fetchSegmentsByMarketId,
  updateCategoryName,
  updateCategoryOrder,
  updateColor,
  updateSegment,
  updateSegmentsOrder,
  updateStatus,
} from "./segments.thunk";
import ShowToaster from "@/toaster";
import { dequal } from "dequal";

const categoriesAdapter = createEntityAdapter<Category, number>({
  // @ts-ignore
  selectId: (category: Category) => category.id,
  sortComparer: (a: Category, b: Category) => a.categoryOrder - b.categoryOrder,
});

const segmentsAdapter = createEntityAdapter<Segment, number>({
  // @ts-ignore
  selectId: (segment: Segment) => segment.id,
  sortComparer: (a: Segment, b: Segment) => a.order - b.order,
});

interface SegmentsState {
  categories: ReturnType<typeof categoriesAdapter.getInitialState>;
  segments: ReturnType<typeof segmentsAdapter.getInitialState>;
  loading: boolean;
  error: string | null;
  /**
   * Set to `true` the first time `fetchSegmentsByMarketId` completes
   * (fulfilled **or** rejected). The `selectionListener` waits for this
   * flag before running `pruneInvalidFields`, ensuring it always has real
   * segment data rather than the empty initial state.
   */
  initialized: boolean;
}

const initialState: SegmentsState = {
  categories: categoriesAdapter.getInitialState(),
  segments: segmentsAdapter.getInitialState(),
  loading: false,
  error: null,
  initialized: false,
};

export const segmentsSlice = createSlice({
  name: "segments",
  initialState,
  reducers: {
    updateAllSegment: (state, action: PayloadAction<any>) => {
      if (action.payload.segments) {
        segmentsAdapter.setAll(state.segments, action.payload.segments);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSegmentsByMarketId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSegmentsByMarketId.fulfilled,
        (state, action: PayloadAction<NormalizedSegments>) => {
          state.loading = false;
          state.error = null;
          state.initialized = true;
          categoriesAdapter.setAll(state.categories, action.payload.categories);
          segmentsAdapter.setAll(state.segments, action.payload.segments);
        }
      )
      .addCase(fetchSegmentsByMarketId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load filters";
        state.initialized = true; // mark ready even on error so listener never blocks forever
      })
      .addCase(createSegmentAfterValidation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createSegmentAfterValidation.fulfilled,
        (state, action: PayloadAction<NormalizedSegments>) => {
          state.loading = false;
          state.error = null;
          categoriesAdapter.setAll(state.categories, action.payload.categories);
          segmentsAdapter.setAll(state.segments, action.payload.segments);
        }
      )
      .addCase(createSegmentAfterValidation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load filters";
      })
      .addCase(updateCategoryOrder.fulfilled, (state, action) => {
        categoriesAdapter.setAll(
          state.categories,
          action.payload.map((categoryData) => ({
            ...state.categories.entities[categoryData.categoryId],
            categoryOrder: categoryData.position,
          }))
        );
      })
      .addCase(updateSegmentsOrder.fulfilled, (state, action) => {
        segmentsAdapter.updateMany(
          state.segments,
          action.payload.map((segmentData) => ({
            id: segmentData.segmentId,
            changes: { order: segmentData.position },
          }))
        );

        state.categories.ids?.forEach((categoryId: number) => {
          const category = state.categories.entities[categoryId];
          const currSegmentOrder = category.segmentIds;
          const reorderedSegmentIds = action.payload
            ?.filter((segmentData) =>
              currSegmentOrder?.includes(segmentData?.segmentId)
            )
            ?.map((segmentData) => segmentData.segmentId);
          if (!dequal(currSegmentOrder, reorderedSegmentIds)) {
            categoriesAdapter.updateOne(state.categories, {
              id: categoryId,
              changes: {
                segmentIds: reorderedSegmentIds,
              },
            });
          }
        });
      })
      .addCase(updateColor.fulfilled, (state, action) => {
        if (action.payload !== null) {
          segmentsAdapter.updateOne(state.segments, {
            id: action.payload.segmentId,
            changes: {
              color: action.payload.color,
            },
          });
        }
      })
      .addCase(updateStatus.fulfilled, (state, action) => {
        if (action.payload !== null) {
          const valuesToUpdate = action.payload.map((value) => ({
            id: value.segmentId,
            changes: {
              isActive: value.isActive,
            },
          }));
          segmentsAdapter.updateMany(state.segments, valuesToUpdate);
        }
      })
      .addCase(createSegment.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSegment.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          if (state.categories?.entities[action.payload.category.id]) {
            segmentsAdapter?.addOne(state.segments, action?.payload?.segment);
            categoriesAdapter?.updateOne(state.categories, {
              id: action?.payload?.category?.id,
              changes: {
                segmentIds: [
                  ...state.categories.entities[action?.payload?.category?.id]
                    ?.segmentIds,
                  action?.payload?.segment?.id,
                ],
              },
            });
          } else {
            categoriesAdapter?.addOne(
              state.categories,
              action?.payload?.category
            );
            segmentsAdapter?.addOne(state.segments, action?.payload?.segment);
          }
          ShowToaster.success("Segment created successfully.");
        }
      })
      .addCase(createSegment.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateSegment.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSegment.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const { segment, category } = action.payload;
          const oldSegment = state?.segments?.entities?.[segment.id];

          if (oldSegment?.categoryId !== segment?.categoryId) {
            const oldCategory = state?.categories?.entities?.[oldSegment?.categoryId];
            if (oldCategory) {
              const newSegmentIds = oldCategory?.segmentIds.filter(
                (id) => id !== segment?.id
              );
              if (newSegmentIds?.length > 0) {
                categoriesAdapter.updateOne(state?.categories, {
                  id: oldCategory?.id,
                  changes: { segmentIds: newSegmentIds },
                });
              } else {
                categoriesAdapter.removeOne(state?.categories, oldCategory?.id);
              }
            }
          }

          if (state?.categories?.entities?.[category?.id]) {
            const segmentExists = state?.categories?.entities?.[
              category?.id
            ]?.segmentIds?.includes(segment?.id);
            if (!segmentExists) {
              categoriesAdapter.updateOne(state?.categories, {
                id: category?.id,
                changes: {
                  segmentIds: [
                    ...state?.categories?.entities?.[category?.id]?.segmentIds,
                    segment?.id,
                  ],
                },
              });
            }
          } else {
            categoriesAdapter.addOne(state?.categories, category);
          }

          segmentsAdapter?.updateOne(state?.segments, {
            id: segment?.id,
            changes: segment,
          });
        }
        ShowToaster.success("Segment updated successfully.");
      })
      .addCase(updateSegment.rejected, (state) => {
        state.loading = false;
      })
      .addCase(deleteSegment.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSegment.fulfilled, (state, action) => {
        if (action.payload?.segmentId) {
          const segmentDetail =
            state.segments.entities[action.payload?.segmentId];
          const categoryDetail =
            state.categories.entities[segmentDetail?.categoryId];
          const segmentIdsAreRemovingDeletedSegment =
            categoryDetail?.segmentIds?.filter(
              (id: number) => id !== action.payload?.segmentId
            );
          if (segmentIdsAreRemovingDeletedSegment?.length === 0) {
            categoriesAdapter.removeOne(state.categories, categoryDetail.id);
          } else {
            categoriesAdapter.updateOne(state.categories, {
              id: categoryDetail.id,
              changes: {
                segmentIds: segmentIdsAreRemovingDeletedSegment,
              },
            });
          }
          segmentsAdapter?.removeOne(state.segments, action.payload.segmentId);
          ShowToaster.success("Segment deleted successfully.");
        }
      })
      .addCase(deleteSegment.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateCategoryName.fulfilled, (state, action) => {
        if (action.payload) {
          categoriesAdapter.updateOne(state.categories, {
            id: action.payload.categoryId,
            changes: { name: action.payload.name },
          });
        }
      });
  },
});

export type SegmentsAdapter = EntityState<Segment, number>;

export type SegmentCategoriesAdapter = EntityState<Category, number>;

export type SegmentsCategoriesAdapterType = {
  segments: EntityState<Segment, number>;
  categories: EntityState<Category, number>;
};

export const { updateAllSegment } = segmentsSlice.actions;


export default segmentsSlice.reducer;
